/**
 *   SENT ──answered──► LIVE ──┬──traded / fired──► FILLED
 *    │                        ├──pulled / lapsed──► CANCELLED
 *    └──refused───────────────┴──refused─────────► REFUSED
 *
 * What the desk sent to a member's broker for one position. SENT is written before the request
 * goes out, since a lost answer is found again only by what the record says was sent: an entry
 * or an exit by its tag, a stop, whose trigger carries none, by its contract, size and level.
 * Nothing is blind-retried and nothing is inferred: only the broker's own row settles an order.
 */
export const ORDER_KINDS = ['ENTRY', 'EXIT', 'STOP'] as const;
export type OrderKind = (typeof ORDER_KINDS)[number];

export const ORDER_STATUSES = ['SENT', 'LIVE', 'FILLED', 'CANCELLED', 'REFUSED'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** What an order ends as; the broker's word, never the desk's guess. */
export type OrderEnding = Exclude<OrderStatus, 'SENT' | 'LIVE'>;

/**
 * What the desk sent may stand at the broker. `listLiveAtBroker` and `listWorkingEntries` read it
 * in SQL — change them together.
 */
export function mayStand(status: OrderStatus): boolean {
  return status === 'SENT' || status === 'LIVE';
}

export function hasEnded(status: OrderStatus): boolean {
  return !mayStand(status);
}

export function isLive(status: OrderStatus | null | undefined): boolean {
  return status === 'LIVE';
}

/** Rows logged before orders were their own, by the action that recorded each kind. */
const RECORDED_BEFORE: Record<string, { kind: OrderKind; unfilled: string }> = {
  'record-entry-order': { kind: 'ENTRY', unfilled: 'REJECTED' },
  'record-exit-order': { kind: 'EXIT', unfilled: 'UNFILLED' },
};

/**
 * What a logged row said the broker did with that kind of order, read off its input: a history
 * reads the log, where the input is as it was written, rows from before orders included.
 */
export function recordedAs(
  row: { actionType: string; input: unknown },
  kind: OrderKind,
): 'filled' | 'unfilled' | null {
  const input = (row.input ?? {}) as Record<string, unknown>;
  const before = RECORDED_BEFORE[row.actionType];
  if (before !== undefined) {
    if (before.kind !== kind) return null;
    if (input.outcome === 'FILLED') return 'filled';
    return input.outcome === before.unfilled ? 'unfilled' : null;
  }
  if (row.actionType !== 'record-order' || input.kind !== kind) return null;
  if (input.outcome === 'FILLED') return 'filled';
  return input.outcome === 'CANCELLED' || input.outcome === 'REFUSED' ? 'unfilled' : null;
}
